import { FirebaseError, getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { PUNTOPY_SITE_ORIGIN } from "@puntopy/contracts";
import {
  createUserWithEmailAndPassword,
  connectAuthEmulator,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
  type Auth,
  type User
} from "firebase/auth";

const configuredAuthDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
const currentHostname = typeof window === "undefined" ? "" : window.location.hostname;
const officialHostname = new URL(PUNTOPY_SITE_ORIGIN).hostname;
const authDomain = currentHostname.endsWith(".web.app") || currentHostname === officialHostname
  ? currentHostname
  : configuredAuthDomain;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

export const firebaseConfigured = Object.values(firebaseConfig).every((value) => typeof value === "string" && value.length > 0);
export const firebaseRequired = import.meta.env.PROD;
export const firebaseApp: FirebaseApp | null = firebaseConfigured && !import.meta.env.SSR
  ? (getApps().length === 0 ? initializeApp(firebaseConfig) : getApp())
  : null;
export const firebaseAuth: Auth | null = firebaseApp === null ? null : getAuth(firebaseApp);
if (import.meta.env.DEV && firebaseAuth !== null && import.meta.env.VITE_AUTH_EMULATOR_URL === "http://127.0.0.1:9099") {
  connectAuthEmulator(firebaseAuth, "http://127.0.0.1:9099", { disableWarnings: true });
}
const googleProvider = new GoogleAuthProvider();
const GOOGLE_REDIRECT_PENDING_KEY = "puntopy:google-redirect-pending";

function verificationActionUrl(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

export function firebaseErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof FirebaseError)) return error instanceof Error ? error.message : fallback;
  const messages: Record<string, string> = {
    "auth/email-already-in-use": "Este correo ya tiene una cuenta. Probá iniciar sesión.",
    "auth/invalid-credential": "El correo o la contraseña no son correctos.",
    "auth/invalid-email": "Ingresá un correo electrónico válido.",
    "auth/popup-blocked": "El navegador bloqueó la ventana de Google. Habilitá las ventanas emergentes y volvé a intentar.",
    "auth/popup-closed-by-user": "Se cerró la ventana de Google antes de completar el acceso.",
    "auth/too-many-requests": "Hubo demasiados intentos. Esperá unos minutos y volvé a intentar.",
    "auth/weak-password": "La contraseña no cumple los requisitos mínimos de seguridad.",
    "auth/network-request-failed": "No se pudo conectar con Firebase. Revisá tu conexión e intentá de nuevo.",
    "auth/user-disabled": "Esta cuenta está deshabilitada. Contactá al equipo de PuntoPY.",
    "auth/unauthorized-domain": "Este dominio no está autorizado para iniciar sesión en Firebase."
  };
  return messages[error.code] ?? fallback;
}

function redirectWasPending(): boolean {
  try {
    return sessionStorage.getItem(GOOGLE_REDIRECT_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

function setRedirectPending(pending: boolean): void {
  try {
    if (pending) sessionStorage.setItem(GOOGLE_REDIRECT_PENDING_KEY, "1");
    else sessionStorage.removeItem(GOOGLE_REDIRECT_PENDING_KEY);
  } catch {
    // Algunos navegadores integrados restringen sessionStorage; Firebase conserva su propio estado.
  }
}

function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const userAgent = navigator.userAgent.toLocaleLowerCase();
  const isTouchIpad = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const isCoarsePointer = typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(pointer: coarse)").matches;
  return /android|iphone|ipad|ipod|iemobile|windows phone|opera mini/.test(userAgent)
    || isTouchIpad
    || isCoarsePointer;
}

export async function signInWithGoogle(): Promise<User | null> {
  if (firebaseAuth === null) throw new Error("Firebase Auth no está configurado.");
  if (isMobileBrowser()) {
    setRedirectPending(true);
    await signInWithRedirect(firebaseAuth, googleProvider);
    return null;
  }
  const result = await signInWithPopup(firebaseAuth, googleProvider);
  return result.user;
}

export async function signInWithEmail(email: string, password: string): Promise<User> {
  if (firebaseAuth === null) throw new Error("Firebase Auth no está configurado.");
  const result = await signInWithEmailAndPassword(firebaseAuth, email.trim(), password);
  return result.user;
}

export async function createEmailAccount(email: string, password: string, displayName: string): Promise<User> {
  if (firebaseAuth === null) throw new Error("Firebase Auth no está configurado.");
  const result = await createUserWithEmailAndPassword(firebaseAuth, email.trim(), password);
  try {
    await updateProfile(result.user, { displayName: displayName.trim() });
  } catch {
    // La identidad ya existe: la ficha de acceso conserva el nombre y debe seguir guardándose.
  }
  return result.user;
}

export async function sendCurrentUserVerification(user: User | null = firebaseAuth?.currentUser ?? null): Promise<void> {
  if (user === null) throw new Error("No hay una cuenta activa para verificar.");
  const url = verificationActionUrl();
  await sendEmailVerification(user, url === undefined ? undefined : { url, handleCodeInApp: false });
}

export async function refreshCurrentUser(): Promise<User> {
  const user = firebaseAuth?.currentUser;
  if (user === null || user === undefined) throw new Error("No hay una sesión activa.");
  await reload(user);
  await user.getIdToken(true);
  return user;
}

export async function sendResetPasswordEmail(email: string): Promise<void> {
  if (firebaseAuth === null) throw new Error("Firebase Auth no está configurado.");
  const url = verificationActionUrl();
  await sendPasswordResetEmail(firebaseAuth, email.trim(), url === undefined ? undefined : { url });
}

export async function completeRedirectSignIn(): Promise<User | null> {
  if (firebaseAuth === null) return null;
  const hadPendingRedirect = redirectWasPending();
  try {
    const result = await getRedirectResult(firebaseAuth);
    const user = result?.user ?? firebaseAuth.currentUser;
    if (user !== null) {
      setRedirectPending(false);
      return user;
    }
    if (hadPendingRedirect) {
      setRedirectPending(false);
      throw new Error("Google regresó, pero el navegador no entregó una sesión válida. Abrí PuntoPY directamente en Chrome y volvé a intentar.");
    }
    return null;
  } catch (error) {
    if (hadPendingRedirect) setRedirectPending(false);
    throw error;
  }
}

export async function signOutFromFirebase(): Promise<void> {
  if (firebaseAuth !== null) await signOut(firebaseAuth);
}

export { onAuthStateChanged };
